/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';

import type { RequestError, RequestOptions } from '@swf/core/api/request';
import type { ClearPayload } from '@swf/core/models/widget';
import type { WidgetModelConfig } from '@swf/core/store';
import type { Entity } from '@swf/generated/analytics/entities';
import type widgets from '@swf/widgets/index';

interface WidgetServices {
  [name: string]: WidgetService;
}

export interface ServiceConfig {
  name: string;
  subscribe?: boolean;
  onLoad?: boolean;
  onAuth?: boolean;
  requestStream?: boolean;
  templateValues?: object;
  method?: RequestOptions['method'];
}

export type ServiceParams = any;

export interface WidgetMeta {
  name: string;
}

export interface WidgetContainer<C = object> {
  id: string;
  config: C;
  layout?: React.CSSProperties & { backgroundFit?: string };
  services?: WidgetServices;
  type: keyof typeof widgets;
  meta: WidgetMeta;
  preload?: boolean;
}

export interface WidgetInstanceProps<C = any, S = any, D = any>
  extends Pick<WidgetContainer, 'id' | 'preload'> {
  config: C;
  /**
   * @deprecated Use widget model effects to fetch data
   */
  services: S;
  /**
   * @deprecated Use widget model selectors to select data
   */
  data?: D;
  style?: React.CSSProperties;
  fetchService(key: string, params?: ServiceParams, meta?: FetchServiceMeta): Promise<string>;
  /**
   * @deprecated Use a widget model to manage subscriptions
   */
  updateSubscriptions(name: string, metas: FetchServiceMeta[]): void;
  /**
   * @deprecated Use a widget model to manage state
   */
  clear(key?: ClearPayload['key']): void;
  /**
   * @deprecated Use a widget model to manage state
   */
  updateState(key: string, data: ReducerPayload['data']): void;
  /**
   * @deprecated Use a widget model to manage state
   */
  updateState(reducer: UpdatePayload<D>['reducer']): void;
}

export type WidgetData = any;

export interface WidgetService {
  params?: object;
  config: ServiceConfig;
}

export interface FetchServiceMeta {
  [key: string]: any;
  patch?: boolean;
  templateValues?: object;
}

type Meta = Record<string, unknown>;

// eslint-disable-next-line @typescript-eslint/ban-types
export interface ReducerPayload<C = {}, D = WidgetData, M = Meta> {
  id: string;
  key: string;
  config: C;
  data: D;
  meta: M;
}

export interface UpdatePayload<D = WidgetData> {
  id: ReducerPayload['id'];
  reducer(state?: D): D | undefined;
}

export type WidgetReducer<
  C = object,
  S = WidgetData,
  D = WidgetData,
  M = Omit<FetchServiceMeta, 'templateValues'>
> = (state: S | undefined, payload: ReducerPayload<C, D, M>) => S | undefined;

export type WidgetErrorHandler<S = WidgetData> = (
  state: S | undefined,
  payload: Omit<ReducerPayload, 'data'> & {
    error: RequestError;
    params: ServiceParams;
  }
) => Promise<S>;

export type WidgetTrackingReducer<S = WidgetServices, D = WidgetData, C = object> = (
  services: S,
  data: D,
  config: C
) => Entity[] | undefined;

export interface WidgetReducers<C = object, S = WidgetData> {
  [key: string]: WidgetReducer<C, S>;
}

export class WidgetComponent<
  P extends WidgetInstanceProps = WidgetInstanceProps,
  S = object,
  SS = any
> extends React.PureComponent<P, S, SS> {
  static serviceReducers?: WidgetReducers;
  static serviceErrorReducers?: WidgetReducers;
  static errorHandler?: WidgetErrorHandler;
  static trackingReducer?: any;
}

export interface WidgetStaticProps<C = any, D = any, S = any> {
  /**
   * @deprecated Use a widget model to manage state
   */
  serviceReducers?: WidgetReducers<C, D>;
  /**
   * @deprecated Use a widget model to manage state
   */
  serviceErrorReducers?: WidgetReducers;
  /**
   * @deprecated Use a widget model to manage state
   */
  errorHandler?: WidgetErrorHandler<D>;
  /**
   * @deprecated Use a widget model selector to get tracking context from widget data
   */
  trackingReducer?: WidgetTrackingReducer<S, D, C>;
  model?: WidgetModelConfig<C, D>;
}

export type WidgetFC<P extends WidgetInstanceProps = WidgetInstanceProps> = React.FC<P> &
  WidgetStaticProps<P['config'], P['data'], P['services']>;
